import React, { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { clearDataCache } from './queryClient.ts';

/**
 * Defence in depth for the in-memory data cache: if the signed-in identity (user, tenant or role)
 * changes without a page reload, everything cached under the previous identity is discarded BEFORE the
 * new identity's screens render.
 *
 * `logout()` already clears the cache, and sign-in performs a full navigation, so this should never fire
 * in practice. It exists because responses differ by role as well as tenant — `/employees` includes
 * `isPregnant` for Admins only — so a cache surviving an identity change would be a disclosure, not just
 * a stale screen.
 */
export const identityKey = (user: unknown, customerId: string | null | undefined, isAdmin: boolean): string => {
  const u = user as { userId?: string; username?: string } | null | undefined;
  return [u?.userId ?? u?.username ?? '', customerId ?? '', isAdmin ? 'admin' : 'user'].join('|');
};

const CacheIdentityGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, customerId, isAdmin } = useAuth();
  const key = identityKey(user, customerId, isAdmin);
  const lastKey = useRef<string | null>(null);

  // Synchronous during render (not in an effect) so no child ever reads the previous identity's rows.
  // (TanStack delivers the resulting observer notifications asynchronously, so this never sets another
  // component's state mid-render.)
  if (lastKey.current !== null && lastKey.current !== key) {
    clearDataCache();
  }
  lastKey.current = key;

  // Keyed by identity so every screen remounts, dropping component-local copies of the old identity's data.
  return <React.Fragment key={key}>{children}</React.Fragment>;
};

export default CacheIdentityGuard;
